import { useCallback, useEffect, useState } from 'react';

const STORAGE_KEY = 'scholar_auto_speak_enabled';
const DEFAULT_ENABLED = true;

/**
 * useAutoSpeakPreference — shared preference for auto-speaking AI responses.
 *
 * Architect directive (2026-05-27): every AI response should auto-speak.
 * Users with reasonable accessibility needs (or noisy environments) can
 * disable via the toggle. Preference syncs across tabs and persists.
 */
function readPref() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (raw === null) return DEFAULT_ENABLED;
    return raw === '1' || raw === 'true';
  } catch {
    return DEFAULT_ENABLED;
  }
}

export default function useAutoSpeakPreference() {
  const [enabled, setEnabled] = useState(readPref);

  useEffect(() => {
    const onStorage = (e) => {
      if (e.key === STORAGE_KEY) setEnabled(readPref());
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  const toggle = useCallback(() => {
    setEnabled((prev) => {
      const next = !prev;
      try {
        localStorage.setItem(STORAGE_KEY, next ? '1' : '0');
      } catch {
        /* storage disabled */
      }
      return next;
    });
  }, []);

  return { autoSpeakEnabled: enabled, toggleAutoSpeak: toggle };
}
